/**
 * PlatformScope — global scope selector for the acting institution/campus.
 *
 * Behaviour by role:
 *   • super_admin  → interactive dropdown (any institution × any campus).
 *                    Selection persisted to localStorage; defaults to their
 *                    bound institution/campus (if any) on first login.
 *   • inst_admin / campus_admin (bound admins) → scope is LOCKED to the
 *                    institution_id / campus_id stamped on their user record.
 *                    Setters are no-ops so they can't accidentally escape their
 *                    scope. Their bound values are still surfaced through
 *                    `institutionId` / `campusId` so the axios interceptor
 *                    (lib/api.js) injects them into every request, keeping the
 *                    UI and API filtered to the correct branch.
 *   • Everyone else → inert context (no scope).
 *
 * The axios interceptor reads scope directly from localStorage, so we mirror
 * whatever value is active (super admin's pick OR bound admin's binding) into
 * the same STORAGE_KEY. That way every request carries the right filter.
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "edukampus.platform_scope";
const PlatformScopeContext = createContext(null);

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { institutionId: null, campusId: null };
    const parsed = JSON.parse(raw);
    return {
      institutionId: parsed.institutionId || null,
      campusId: parsed.campusId || null,
    };
  } catch {
    return { institutionId: null, campusId: null };
  }
}

function persistToStorage(institutionId, campusId) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ institutionId: institutionId || null, campusId: campusId || null }),
    );
  } catch { /* storage may be disabled (privacy mode) — ignore */ }
}

export function PlatformScopeProvider({ children }) {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isSuper = roles.includes("super_admin");
  const userInst = user?.institution_id || null;
  const userCampus = user?.campus_id || null;
  // A user is "bound" when their record carries an institution_id or campus_id.
  // For non-super admins this locks the scope; for super admins it just seeds
  // the initial default (they can still change it).
  const isBound = !isSuper && (!!userInst || !!userCampus);
  const qc = useQueryClient();

  const [institutionId, setInstitutionIdState] = useState(() => loadFromStorage().institutionId);
  const [campusId, setCampusIdState] = useState(() => loadFromStorage().campusId);

  // Sync scope from the authenticated user object as soon as it resolves.
  //   - Bound admin: FORCE scope to their binding every time (they can't roam).
  //   - Super admin: seed from binding only if no saved preference exists.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    if (isBound) {
      // Only update state if it actually changed — avoids re-render loops when
      // the user object references shift but values stay the same.
      setInstitutionIdState((prev) => (prev === userInst ? prev : userInst));
      setCampusIdState((prev) => (prev === userCampus ? prev : userCampus));
      persistToStorage(userInst, userCampus);
      return;
    }
    if (isSuper) {
      const saved = loadFromStorage();
      if (!saved.institutionId && !saved.campusId && (userInst || userCampus)) {
        setInstitutionIdState(userInst);
        setCampusIdState(userCampus);
        persistToStorage(userInst, userCampus);
      }
    }
  }, [user, isSuper, isBound, userInst, userCampus]);

  // Persist super-admin picks + invalidate cached queries so pages re-fetch
  // with the new institution_id / campus_id params.
  useEffect(() => {
    if (!isSuper) return;
    persistToStorage(institutionId, campusId);
    qc.invalidateQueries();
  }, [institutionId, campusId, isSuper, qc]);

  const setInstitutionId = useCallback((id) => {
    if (isBound) return; // bound admin — scope is locked
    setInstitutionIdState(id || null);
    // Changing institution clears the campus selection — cascading behaviour.
    setCampusIdState(null);
  }, [isBound]);

  const setCampusId = useCallback((id) => {
    if (isBound) return;
    setCampusIdState(id || null);
  }, [isBound]);

  const clear = useCallback(() => {
    if (isBound) return;
    setInstitutionIdState(null);
    setCampusIdState(null);
  }, [isBound]);

  const value = useMemo(
    () => ({
      isSuper,
      isBound,
      institutionId,
      campusId,
      setInstitutionId,
      setCampusId,
      clear,
    }),
    [isSuper, isBound, institutionId, campusId, setInstitutionId, setCampusId, clear],
  );

  return (
    <PlatformScopeContext.Provider value={value}>
      {children}
    </PlatformScopeContext.Provider>
  );
}

export function usePlatformScope() {
  const ctx = useContext(PlatformScopeContext);
  if (!ctx) {
    // Safe fallback if a page renders outside the provider (e.g. Login).
    return {
      isSuper: false,
      isBound: false,
      institutionId: null,
      campusId: null,
      setInstitutionId: () => {},
      setCampusId: () => {},
      clear: () => {},
    };
  }
  return ctx;
}
